import type {
  Company,
  CompanyListResponse,
  CompanyResponse,
} from '~/types/__generated__'
import type { Strapi4RequestParams } from '~/types/strapi'

const FULL_COMPANY_POPULATE = [
  'logo',
  'address',
  'about',
  'about.info',
  'about.socials',
  'about.whyWorkWithUs',
  'about.publicEmployees',
  'about.publicEmployees.candidate',
  'about.publicEmployees.candidate.assessments',
  'about.publicEmployees.candidate.assessments.assessmentType',
  'about.publicEmployees.candidate.assessments.tasks',
  'about.publicEmployees.candidate.avatar',
  'about.publicEmployees.publicAssessments',
  'about.publicEmployees.publicAssessments.data',
  'about.publicEmployees.publicAssessments.data.subdata',
  'about.publicEmployees.publicAssessments.assessmentType',
  'embeds',
  'media',
  'media.items',
  'highlights',
  'highlights.items',
  'tags',
  'tags.parent',
  'tags.parent.parent',
  'tags.children',
  'users',
  'users.contact',
  'vacancies',
  'vacancies.tags',
  'vacancies.tags.parent',
  'vacancies.tags.children',
  'vacancies.image',
  'departments',
  'locations',
  'interactions',
  'interactions.professional',
  'profile',
  'profile.education',
  'profile.education.from',
  'profile.education.to',
  'profile.personality',
  'profile.motivationsRanking',
  'profile.rolesRanking',
  'profile.topCultureRanking',
  'profile.minCultureRanking',
  'filterTags',
  'filterTags.tag',
]

const OVERVIEW_COMPANY_POPULATE = ['logo', 'address', 'media', 'media.items']

export function useApiCompany() {
  const user = useStrapiUser()
  const client = useStrapiClient()

  async function getMyCompany(query: Strapi4RequestParams = {}) {
    const myCompanyFilter = { users: { id: { $eq: user.value?.id } } }
    const { data } = await client<CompanyListResponse>('/companies', {
      method: 'GET',
      params: {
        populate: FULL_COMPANY_POPULATE,
        ...query,
        filters: {
          $and: [myCompanyFilter, ...(query?.filters ? [query?.filters] : [])],
        },
      },
    })
    return data?.[0]
  }

  async function getCompanyBySlug(
    slug: string,
    query: Strapi4RequestParams = {},
  ) {
    const { data } = await client<CompanyResponse>(`/companies/slug/${slug}`, {
      method: 'GET',
      params: {
        populate: FULL_COMPANY_POPULATE,
        useGeoFilter: false,
        useTagFilter: false,
        useInteractionsFilter: false,
        ...query,
      },
    })
    return data
  }

  async function getCompany(
    id: number | string,
    query: Strapi4RequestParams = {},
  ) {
    const { data } = await client<CompanyResponse>(`/companies/${id}`, {
      method: 'GET',
      params: {
        populate: FULL_COMPANY_POPULATE,
        ...query,
      },
    })
    return data
  }

  async function getCompanies(query: Strapi4RequestParams = {}) {
    const response = await client<CompanyListResponse>('/companies', {
      method: 'GET',
      params: {
        populate: OVERVIEW_COMPANY_POPULATE,
        ...query,
      },
    })

    return response
  }

  async function updateCompany(
    id: number | string,
    body: Partial<Company>,
    query: Strapi4RequestParams = {},
  ) {
    const { data } = await client<CompanyResponse>(`/companies/${id}`, {
      method: 'PUT',
      body,
      params: {
        populate: FULL_COMPANY_POPULATE,
        ...query,
      },
    })
    return data
  }

  async function getCompanyCandidateTeams() {
    const { data } = await client<any>(`/companies/candidate-teams`, {})

    return data
  }

  async function createCompanyCandidateTeam(name: string) {
    const { data } = await client<any>(`/companies/candidate-teams`, {
      method: 'POST',
      body: { data: { name } },
    })

    return data
  }

  return {
    getMyCompany,
    getCompanyBySlug,
    getCompany,
    updateCompany,
    getCompanies,
    getCompanyCandidateTeams,
    createCompanyCandidateTeam,
  }
}
